import {
  Calendar,
  Check,
  CornerDownRight,
  Inbox,
  type LucideIcon,
  Minus,
  Pause,
  Play,
  Sun,
  TriangleAlert,
  User,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { localDay } from '../../shared/calendar';
import type { FocusSession } from '../../shared/focus';
import type { LocalRecord, Task } from '../../shared/model';
import { taskPersonName } from '../../shared/productivity';

import { relativeDay, sinceDay } from '../lib/day-words';

export type StatusTone =
  | 'neutral'
  | 'priority'
  | 'info'
  | 'focus'
  | 'success'
  | 'attention'
  | 'danger';

/**
 * A labelled status chip, the phone's `Pill`: a capsule in its role's colour,
 * always with words, and a symbol that repeats them when there is one.
 */
export function StatusBadge({
  tone = 'neutral',
  icon: Icon,
  children,
  title,
}: {
  tone?: StatusTone;
  icon?: LucideIcon;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className={`status-badge tone-${tone}`} title={title}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </span>
  );
}

export type Badge = { tone: StatusTone; label: string; icon: LucideIcon };

/**
 * A task's badges, in the phone's order and words: working or on a break,
 * then completed, dropped, or the deadline and who is waiting. Only live work
 * is told it is overdue, and days are said as a person would say them.
 */
export function taskBadges(
  task: Task,
  id: string,
  records: LocalRecord[] = [],
  today = localDay(),
): Badge[] {
  const badges: Badge[] = [];
  const status = taskStatus(task, id, records);

  if (status.tone === 'focus') {
    badges.push({ tone: 'focus', label: 'Working', icon: Play });
  } else if (status.tone === 'attention') {
    badges.push({ tone: 'attention', label: 'On a break', icon: Pause });
  }

  if (task.done) {
    badges.push({ tone: 'success', label: 'Completed', icon: Check });
  } else if (task.dropped) {
    badges.push({ tone: 'neutral', label: 'Dropped', icon: Minus });
  } else {
    if (task.dueDate) {
      badges.push(
        task.dueDate < today
          ? {
              tone: 'danger',
              label: `Overdue since ${sinceDay(task.dueDate, today)}`,
              icon: TriangleAlert,
            }
          : task.dueDate === today
            ? { tone: 'priority', label: 'Due today', icon: Calendar }
            : { tone: 'neutral', label: `Due ${relativeDay(task.dueDate, today)}`, icon: Calendar },
      );
    }

    const waiting = taskPersonName(task, records);

    if (waiting) {
      badges.push({ tone: 'attention', label: `${waiting} is waiting`, icon: User });
    }
  }

  return badges;
}

/**
 * Why a task is worth choosing today, with the tone and symbol its badge
 * carries: a deadline within the week before someone waiting, and someone
 * waiting before a later deadline, as the suggestions are ranked.
 */
export function taskReason(task: Task, id: string, records: LocalRecord[], today = localDay()) {
  const badges = taskBadges(task, id, records, today);
  const due = badges.find(b => b.icon === TriangleAlert || b.icon === Calendar);
  const waiting = badges.find(b => b.icon === User);

  const near = Boolean(
    task.dueDate &&
      new Date(`${task.dueDate}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime() <=
        7 * 86_400_000,
  );

  return (
    (near ? due : (waiting ?? due)) ?? {
      tone: 'neutral' as const,
      label: 'A concrete next step',
      icon: CornerDownRight,
    }
  );
}

export function taskStatus(
  task: Task,
  id: string,
  records: LocalRecord[] = [],
): { tone: StatusTone; label: string } {
  if (task.done) {
    return { tone: 'success', label: 'Completed' };
  }

  if (task.dropped) {
    return { tone: 'neutral', label: 'Dropped' };
  }

  const sessions = records
    .filter(r => r.kind === 'focus' && (r.value as FocusSession).taskId === id)
    .map(r => r.value as FocusSession);

  const open = sessions.filter(s => s.endedAt === null);

  const running = sessions.length
    ? open.some(s => s.segments.some(segment => segment.endedAt === null))
    : Boolean(task.focus?.runningSince);

  if (running) {
    return { tone: 'focus', label: 'Working' };
  }

  if (sessions.length ? open.length > 0 : task.focus?.sessionOpen) {
    return { tone: 'attention', label: 'On a break' };
  }

  return task.today ? { tone: 'priority', label: 'Today' } : { tone: 'info', label: 'Inbox' };
}

export function TaskBadges({
  record,
  records = [],
  placement = false,
  except = [],
}: {
  record: Pick<LocalRecord, 'id' | 'value'>;
  records?: LocalRecord[];
  /** Adds Today or Inbox, where a list has no other way of saying which. */
  placement?: boolean;
  /** Labels a surrounding panel already says, such as its session state. */
  except?: string[];
}) {
  const task = record.value as Task;
  const status = taskStatus(task, record.id, records);

  const badges = taskBadges(task, record.id, records).filter(b => !except.includes(b.label));

  if (placement && (status.tone === 'priority' || status.tone === 'info')) {
    badges.unshift({
      tone: status.tone,
      label: status.label,
      icon: status.tone === 'priority' ? Sun : Inbox,
    });
  }

  if (!badges.length) {
    return null;
  }

  return (
    <span className="status-badges">
      {badges.map(badge => (
        <StatusBadge key={badge.label} tone={badge.tone} icon={badge.icon}>
          {badge.label}
        </StatusBadge>
      ))}
    </span>
  );
}
